import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, refreshAccessToken, session, unwrap } from '../api/client';
import { AppError } from '../api/errors';
import type { components } from '../api/schema';

export type CurrentUser = components['schemas']['CurrentUser'];
export type Permission = components['schemas']['Permission'];

export type AuthState =
  { kind: 'unknown' } | { kind: 'anonymous' } | { kind: 'authenticated'; user: CurrentUser };

interface AuthContext {
  state: AuthState;
  user: CurrentUser | null;
  has: (permission: Permission) => boolean;
  login: (email: string, password: string) => Promise<{ challengeId: string; expiresAt: string }>;
  verifyTotp: (challengeId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Cere un token de step-up (cod TOTP proaspăt); îl păstrează pentru `withStepUp`. */
  stepUp: (code: string) => Promise<void>;
  /** Rulează `fn` cu header-ul de step-up; dacă nu există token valid, deschide dialogul și așteaptă codul. */
  withStepUp: <T>(fn: (headers: { 'X-Step-Up-Token': string }) => Promise<T>) => Promise<T>;
  /** Dialogul de step-up (randat de Shell). */
  stepUpRequest: {
    resolve: (code: string) => void;
    reject: (e: Error) => void;
    error?: string;
  } | null;
}

const Ctx = createContext<AuthContext | null>(null);

export function AuthProvider({
  children,
  skipInitialRefresh = false,
  initialUser,
}: {
  children: ReactNode;
  /** Teste: fără `POST /auth/refresh` la pornire. */
  skipInitialRefresh?: boolean;
  /** Teste: sesiune deja autentificată. */
  initialUser?: CurrentUser | null;
}) {
  const [state, setState] = useState<AuthState>(
    initialUser
      ? { kind: 'authenticated', user: initialUser }
      : skipInitialRefresh
        ? { kind: 'anonymous' }
        : { kind: 'unknown' },
  );

  const stepUpToken = useRef<{ token: string; expiresAt: number } | null>(null);
  const [stepUpRequest, setStepUpRequest] = useState<AuthContext['stepUpRequest']>(null);

  const loadMe = useCallback(async () => {
    const me = await unwrap(api.GET('/auth/me'));
    setState({ kind: 'authenticated', user: me });
  }, []);

  useEffect(() => {
    session.onExpired = () => {
      stepUpToken.current = null;
      setState({ kind: 'anonymous' });
    };
    if (skipInitialRefresh) return;
    (async () => {
      const ok = await refreshAccessToken();
      if (!ok) {
        setState({ kind: 'anonymous' });
        return;
      }
      try {
        await loadMe();
      } catch {
        setState({ kind: 'anonymous' });
      }
    })();
  }, [loadMe, skipInitialRefresh]);

  const login = useCallback(async (email: string, password: string) => {
    return unwrap(api.POST('/auth/login', { body: { email, password } }));
  }, []);

  const verifyTotp = useCallback(
    async (challengeId: string, code: string) => {
      const tokens = await unwrap(api.POST('/auth/login/totp', { body: { challengeId, code } }));
      session.accessToken = tokens.accessToken;
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await api.POST('/auth/logout');
    } finally {
      session.accessToken = null;
      stepUpToken.current = null;
      setState({ kind: 'anonymous' });
    }
  }, []);

  const stepUp = useCallback(async (code: string) => {
    const r = await unwrap(api.POST('/auth/step-up', { body: { code } }));
    stepUpToken.current = { token: r.stepUpToken, expiresAt: new Date(r.expiresAt).getTime() };
  }, []);

  const askCode = useCallback(
    (error?: string) =>
      new Promise<string>((resolve, reject) => {
        setStepUpRequest({ resolve, reject, error });
      }),
    [],
  );

  const withStepUp = useCallback(
    async <T,>(fn: (headers: { 'X-Step-Up-Token': string }) => Promise<T>): Promise<T> => {
      let error: string | undefined;
      for (let attempt = 0; attempt < 3; attempt++) {
        let current = stepUpToken.current;
        if (!current || current.expiresAt <= Date.now()) {
          const code = await askCode(error);
          try {
            await stepUp(code);
          } catch (e) {
            error = e instanceof AppError ? e.userMessage : 'Cod invalid';
            continue;
          }
          current = stepUpToken.current!;
        }
        // Tokenul e de unică folosință: îl consumăm acum.
        stepUpToken.current = null;
        try {
          return await fn({ 'X-Step-Up-Token': current.token });
        } catch (e) {
          if (e instanceof AppError && e.code === 'step-up-required') {
            error = 'Confirmarea a expirat. Introdu un cod nou.';
            continue;
          }
          throw e;
        }
      }
      throw new AppError(
        401,
        'step-up-required',
        'Confirmare suplimentară necesară',
        'Prea multe încercări de confirmare.',
      );
    },
    [askCode, stepUp],
  );

  const value = useMemo<AuthContext>(
    () => ({
      state,
      user: state.kind === 'authenticated' ? state.user : null,
      has: (p) => state.kind === 'authenticated' && state.user.permissions.includes(p),
      login,
      verifyTotp,
      logout,
      stepUp,
      withStepUp,
      stepUpRequest: stepUpRequest
        ? {
            ...stepUpRequest,
            resolve: (code: string) => {
              setStepUpRequest(null);
              stepUpRequest.resolve(code);
            },
            reject: (e: Error) => {
              setStepUpRequest(null);
              stepUpRequest.reject(e);
            },
          }
        : null,
    }),
    [state, login, verifyTotp, logout, stepUp, withStepUp, stepUpRequest],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth în afara AuthProvider');
  return ctx;
}
